import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./awardDeck.html";
import { Routes } from '~/modules/routes';
import { navigate } from '~/modules/stores/routerStore';
import { WireI18n } from '~/modules/stores/i18nStore';
import { upsertQueryParams } from '~/modules/utilities/router';

/** One award card in the deck. */

/** Overview tab code and the scroll anchor the deck jumps to within it. */
const OVERVIEW_TAB_CODE = 'e';
const ACHIEVEMENTS_SCROLL_TARGET = 'achievements';

/** A card positioned for render: its resting pose plus the source data. */

/** How long each card rests at the front before the deck cycles it to the back. */
const CYCLE_MS = 2800;

/**
 * Register the per-card pose properties as typed, animatable values so the deck
 * can transition them individually (see awardDeck.css). Registration is global
 * and one-time; re-registering throws, and the API is absent server-side, so
 * both are swallowed. A scoped `@property` rule would not reach this registry.
 */
function registerCardProperties() {
  const registry = globalThis.CSS;
  if (!registry?.registerProperty) {
    return;
  }
  const definitions = [{
    name: '--card-x',
    syntax: '<length>',
    initialValue: '0px'
  }, {
    name: '--card-y',
    syntax: '<length>',
    initialValue: '0px'
  }, {
    name: '--card-rotate',
    syntax: '<angle>',
    initialValue: '0deg'
  }, {
    name: '--card-scale',
    syntax: '<number>',
    initialValue: '1'
  }];
  for (const {
    name,
    syntax,
    initialValue
  } of definitions) {
    try {
      registry.registerProperty({
        name,
        syntax,
        inherits: false,
        initialValue
      });
    } catch {
      // Already registered by an earlier deck instance — nothing to do.
    }
  }
}

/**
 * A shuffling stack of award badges shown on a listing when its provider has
 * earned achievements. The front card rests upright; cards behind it peek
 * up-and-left, rotated. On an interval the front card cycles to the back, so
 * each award takes its turn on top.
 *
 * Motion is suppressed under `prefers-reduced-motion`: the deck renders its
 * resting stack once and never cycles.
 */
class AwardDeck extends LightningElement {
  constructor(...args) {
    super(...args);
    this.i18n = void 0;
    this._cards = [];
    /** Indices into `_cards`; `order[0]` is the front-most card. */
    this.order = [];
    this.cycleIntervalId = null;
  }
  get cards() {
    return this._cards;
  }
  set cards(next) {
    this._cards = Array.isArray(next) ? next : [];
    // Reset the ordering to match the new deck, front card first.
    this.order = this._cards.map((_, i) => i);
    this.restartCycle();
  }
  connectedCallback() {
    registerCardProperties();
  }

  /** Accessible label for the deck's activation control. */
  get viewAchievementsLabel() {
    return this.i18n?.heading?.viewProviderAchievements ?? '';
  }

  /**
   * Open the Overview tab and scroll to the provider achievements section. The
   * section lives in a different component's shadow tree, so we route through
   * the shared `scrollTo` query param rather than reaching across shadow roots;
   * the overview panel consumes it in its `renderedCallback`.
   */
  handleActivate() {
    navigate(`/${Routes.LISTING}?${upsertQueryParams(['tab', OVERVIEW_TAB_CODE], ['scrollTo', ACHIEVEMENTS_SCROLL_TARGET])}`);
  }

  /** Activate on Enter or Space so the deck behaves like a native button. */
  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleActivate();
    }
  }
  disconnectedCallback() {
    this.stopCycle();
  }

  /**
   * Cards paired with the resting pose for their current depth. Rendered in a
   * stable order (card identity), never reordered, so each card's element keeps
   * its place among its siblings and only its pose changes. Depth (0 = front)
   * comes from the card's slot in `order`; paint order is the z-index in the
   * pose. Keeping the DOM order fixed lets the CSS transition carry every card
   * from its old depth to its new one, so the whole stack shuffles together.
   */
  get positionedCards() {
    return this._cards.map((card, cardIndex) => ({
      ...card,
      style: this.poseForDepth(this.order.indexOf(cardIndex))
    }));
  }

  /**
   * Resting pose for a card at `depth` in the deck (0 = front). Cards behind
   * shift up-and-left, rotate, and shrink slightly so the front card reads as
   * closest. Expressed as CSS custom properties consumed by awardDeck.css.
   */
  poseForDepth(depth) {
    const x = depth * -5;
    const y = depth * -4;
    const rotate = depth === 0 ? 0 : -10;
    const scale = 1 - depth * 0.04;
    const zIndex = this._cards.length - depth;
    return `--card-x: ${x}px; --card-y: ${y}px; --card-rotate: ${rotate}deg; --card-scale: ${scale}; z-index: ${zIndex};`;
  }
  restartCycle() {
    this.stopCycle();
    // Nothing to cycle through with a single card, and no motion when the
    // viewer prefers reduced motion — the resting stack renders statically.
    if (this._cards.length < 2 || this.prefersReducedMotion()) {
      return;
    }
    this.cycleIntervalId = setInterval(() => {
      // Move the front card to the back; the rest shift forward one depth.
      this.order = [...this.order.slice(1), this.order[0]];
    }, CYCLE_MS);
  }
  stopCycle() {
    if (this.cycleIntervalId) {
      clearInterval(this.cycleIntervalId);
      this.cycleIntervalId = null;
    }
  }
  prefersReducedMotion() {
    try {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    } catch {
      return false;
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(AwardDeck, {
  publicProps: {
    cards: {
      config: 3
    }
  },
  track: {
    order: 1
  },
  wire: {
    i18n: {
      adapter: WireI18n,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["_cards", "cycleIntervalId"]
});
const __lwc_component_class_internal = _registerComponent(AwardDeck, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;